import React, { useEffect, useState } from "react";
import { useAppContext } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { MembershipAPI } from "../../api/membership";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { VisitorsAPI } from "../../api/visitors";
import { GridCloseIcon } from "@mui/x-data-grid";
import { emailRegex } from "../constant";
import {
  memberOptionGroup,
  memberOptionLabel,
  sortMembersDepartedLast,
} from "../helper";
import renderMemberOption from "../MemberOption";

const AddVisitorsModal = ({ open, handleClose, setIsLoading }) => {
  const { openSnack } = useAppContext();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);

  const [formState, setFormState] = useState({
    visitor_first_name: "",
    visitor_last_name: "",
    date_visited: dayjs(new Date()),
    chair: "",
    phone_number: "",
    email_address: "",
    member_who_brought_visitor_id: "",
  });
  const [formError, setFormError] = useState({});

  const handleError = (formObj = formState) => {
    const temp = { ...formError };

    if ("visitor_first_name" in formObj) {
      temp.visitor_first_name = formObj.visitor_first_name
        ? ""
        : "Visitor first name is required.";
    }

    if ("visitor_last_name" in formObj) {
      temp.visitor_last_name = formObj.visitor_last_name
        ? ""
        : "Visitor last name is required.";
    }

    if ("date_visited" in formObj) {
      temp.date_visited = formObj.date_visited
        ? ""
        : "Date visited is required.";
    }

    if ("chair" in formObj) {
      temp.chair = formObj.chair ? "" : "Chair is required.";
    }

    if ("member_who_brought_visitor_id" in formObj) {
      temp.member_who_brought_visitor_id = formObj.member_who_brought_visitor_id
        ? ""
        : "Member who brought visitor is required.";
    }

    if ("email_address" in formObj) {
      if (formObj.email_address !== "") {
        temp.email_address = !emailRegex?.test(formObj.email_address)
          ? "Please enter valid Email Address!"
          : "";
      } else {
        temp.email_address = "";
      }
    }

    setFormError({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleChange = (key, value) => {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
    handleError({ [key]: value });
  };

  const fetchMembers = async () => {
    try {
      const response = await MembershipAPI.getMembers("");
      setMembers(response?.items || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderForm = () => {
    return (
      <Grid
        container
        spacing={2}
        rowSpacing={2.5}
        sx={{
          padding: "16px 0",
        }}
      >
        <Grid item xs={12}>
          <Autocomplete
            value={formState?.member_who_brought_visitor_id || null}
            onChange={(_e, newValue) =>
              handleChange("member_who_brought_visitor_id", newValue)
            }
            id="member-who-brought-visitor"
            options={sortMembersDepartedLast(members)}
            groupBy={memberOptionGroup}
            getOptionLabel={memberOptionLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={renderMemberOption}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Member Who Brought Visitor*"
                className="login-textField"
                size="small"
                error={!!formError.member_who_brought_visitor_id}
                helperText={formError.member_who_brought_visitor_id}
              />
            )}
            className="login-textField"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="normal"
            fullWidth
            id="visitor_first_name"
            label="Visitor First Name*"
            name="visitor_first_name"
            value={formState?.visitor_first_name || ""}
            onChange={(e) => handleChange("visitor_first_name", e.target.value)}
            className="login-textField"
            size="small"
            error={!!formError.visitor_first_name}
            helperText={formError.visitor_first_name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="normal"
            fullWidth
            id="visitor_last_name"
            label="Visitor Last Name*"
            name="visitor_last_name"
            value={formState?.visitor_last_name || ""}
            onChange={(e) => handleChange("visitor_last_name", e.target.value)}
            className="login-textField"
            size="small"
            error={!!formError.visitor_last_name}
            helperText={formError.visitor_last_name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Date Visited*"
            value={formState?.date_visited || null}
            onChange={(newValue) => handleChange("date_visited", newValue)}
            className="login-textField date-textField"
            sx={{ width: "100%", height: "40px" }}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                margin: "normal",
                fullWidth: true,
                id: "date_visited",
                name: "date_visited",
                className: "login-textField",
                size: "small",
                error: !!formError.date_visited,
                helperText: formError.date_visited,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="normal"
            fullWidth
            id="chair"
            label="Chair*"
            name="chair"
            value={formState?.chair || ""}
            onChange={(e) => handleChange("chair", e.target.value)}
            className="login-textField"
            size="small"
            error={!!formError.chair}
            helperText={formError.chair}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="normal"
            fullWidth
            id="phone_number"
            label="Phone Number"
            name="phone_number"
            value={formState?.phone_number || ""}
            onChange={(e) => handleChange("phone_number", e.target.value)}
            className="login-textField"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="normal"
            fullWidth
            id="email_address"
            label="Email Address"
            name="email_address"
            value={formState?.email_address || ""}
            onChange={(e) => handleChange("email_address", e.target.value)}
            className="login-textField"
            size="small"
            error={!!formError.email_address}
            helperText={formError.email_address}
          />
        </Grid>
      </Grid>
    );
  };

  const AddVisitor = async (form) => {
    const formObj = {
      ...form,
      date_visited: dayjs(form?.date_visited)?.format("YYYY-MM-DD"),
      member_who_brought_visitor_id: form?.member_who_brought_visitor_id?.id,
    };

    !formObj?.email_address && delete formObj?.email_address;
    !formObj?.phone_number && delete formObj?.phone_number;
    try {
      await VisitorsAPI.addVisitor(formObj);
      openSnack("Visitor created successfully.", "success");
      setIsLoading(true);
      handleClose();
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  const handleSubmit = () => {
    if (handleError()) {
      AddVisitor(formState);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={(_e, reason) =>
        reason === "backdropClick" ? "" : handleClose()
      }
      hideBackdrop
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="custom-modal"
      sx={{
        ".MuiPaper-root": {
          width: "834px",
        },
      }}
    >
      <div className="modal-header">
        <h2 className="header-text">Create New Visitor</h2>
        <IconButton onClick={() => handleClose()}>
          <GridCloseIcon />
        </IconButton>
      </div>
      {renderForm()}

      <DialogActions
        sx={{ paddingTop: `${formError?.dues_paid_date ? "24px" : "8px"}` }}
        className="modal-btn-container"
      >
        <Button onClick={handleClose} className="button-common light-blue-bg">
          Cancel
        </Button>
        <Button onClick={handleSubmit} className="add-new-button button-common">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddVisitorsModal;
